import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import Cookies from "universal-cookie";
import toast from "react-hot-toast";
import useGet from "../read/useGet";
import usePut from "../put/usePut";
import { COMPANY_ID } from "../../config";

const cookies = new Cookies();

export const useAdminPettyCashDetail = ({ employeeId, onApprovalSuccess, onDeleteSuccess }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = cookies.get(COMPANY_ID);

  const initialFilters = {
    limit: 10,
    page_number: parseInt(searchParams.get("page_number")) || 0,
    search_query: searchParams.get("search_query") || "",
    from_date: searchParams.get("from_date") || "",
    to_date: searchParams.get("to_date") || "",
    approval_status: searchParams.get("approval_status") || "",
    company_employee_id: employeeId,
  };

  const [searchQuery, setSearchQuery] = useState(initialFilters.search_query);
  const [selectedTxnId, setSelectedTxnId] = useState(null);

  // 1. Fetch transaction listing for this employee
  const {
    list: transactionListing,
    paramsObject: filters,
    setGetListParams: setFilters,
  } = useGet({
    url: "petty-cash/transaction_listing",
    initialData: initialFilters,
    enabled: !!employeeId,
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // 2. Fetch Petty Cash Statistics for this employee
  const { list: statisticsListing } = useGet({
    url: "petty-cash/statistics",
    initialData: { company_employee_id: employeeId },
    enabled: !!employeeId,
  });

  const employeeInfo = statisticsListing.data?.data?.employee_data;

  // 3. Approval Mutation (Approve or Reject)
  const { handleAuth: updateApprovalStatusMutation } = usePut({
    url: "petty-cash/approval_transaction",
    refreshUrl: ["petty-cash/transaction_listing", "petty-cash/statistics"],
    onSuccess: () => {
      toast.success("Transaction status updated successfully");
      onApprovalSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to update status");
    },
  });

  const { mutate: updateApprovalStatus, isLoading: isProcessingApproval } = updateApprovalStatusMutation;

  const updateApprovalStatusHandler = ({ transactionId, status, remarks }) => {
    updateApprovalStatus({
      transaction_id: transactionId,
      approval_status: status,
      ...(remarks && { remarks }),
    });
  };

  // 4. Delete Transaction Mutation
  const { handleAuth: deleteTransactionMutation } = usePut({
    url: "petty-cash/delete_transaction",
    refreshUrl: ["petty-cash/transaction_listing", "petty-cash/statistics"],
    onSuccess: () => {
      toast.success("Transaction Deleted Successfully");
      onDeleteSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete transaction");
    },
  });

  const { mutate: deleteTransaction, isLoading: isDeleting } = deleteTransactionMutation;

  const deleteTransactionHandler = (transactionId) => {
    deleteTransaction({
      transaction_id: transactionId,
      company_employee_id: employeeId,
    });
  };

  // 5. Export Report Mutation (Admin exports for this employee)
  const { handleAuth: exportReportMutation } = usePut({
    url: "petty-cash/transaction_report",
    axiosConfig: { responseType: "blob" },
    onSuccess: (res) => {
      const href = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = href;
      a.download = `Petty_Cash_Transaction_Report_${employeeInfo?.employee_name || "Employee"}.xlsx`;
      a.click();
      toast.success("Report downloaded successfully");
    },
    onError: async (err) => {
      let message = "Failed to export report";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || message;
        } catch { }
      }
      toast.error(message);
    },
  });

  const { mutate: exportReport, isLoading: isExporting } = exportReportMutation;

  const exportReportHandler = () => {
    exportReport({
      ...(companyId && { company_id: companyId }),
      ...(employeeId && { company_employee_id: employeeId }),
      ...(filters.from_date && { from_date: filters.from_date }),
      ...(filters.to_date && { to_date: filters.to_date }),
      ...(filters.approval_status && { approval_status: filters.approval_status }),
    });
  };

  // 6. Fetch Single Transaction Details for view modal
  const {
    list: activeTxnQuery,
    setGetListParams: setActiveTxnParams,
  } = useGet({
    url: "petty-cash/fetch_single_transaction",
    initialData: { transaction_id: selectedTxnId },
    enabled: !!selectedTxnId,
  });

  const handleViewDetails = (txnId) => {
    setSelectedTxnId(txnId);
    setActiveTxnParams({ transaction_id: txnId });
  };

  const handlePageClick = (val) => {
    const updated = { ...filters, page_number: val.selected };
    setFilters(updated);
    const { company_employee_id, ...searchParamsObj } = updated;
    setSearchParams(searchParamsObj);
  };

  const handleFilterChange = (field, value) => {
    const updated = { ...filters, [field]: value, page_number: 0 };
    setFilters(updated);
    const { company_employee_id, ...searchParamsObj } = updated;
    setSearchParams(searchParamsObj);
  };

  const debouncedSearch = useRef(
    debounce((value) => {
      const updated = { ...filtersRef.current, search_query: value, page_number: 0 };
      setFilters(updated);
      const { company_employee_id, ...searchParamsObj } = updated;
      setSearchParams(searchParamsObj);
    }, 300)
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  return {
    transactionListing,
    statisticsListing,
    employeeInfo,
    activeTxnQuery,
    filters,
    searchQuery,
    selectedTxnId,
    setSelectedTxnId,
    isProcessingApproval,
    isDeleting,
    isExporting,
    handleViewDetails,
    updateApprovalStatusHandler,
    deleteTransactionHandler,
    exportReportHandler,
    handlePageClick,
    handleFilterChange,
    handleSearchChange,
  };
};
